import {
  getDefaultThemeModeId,
  getThemeModeDefinition,
  isThemeModeId,
  validateThemeTokens,
  THEME_TOKEN_KEYS
} from '../../ui/theme_modes.js';
import {
  registerThemeConsumer,
  unregisterThemeConsumer
} from '../../ui/theme_consumers.js';

const SHELL_THEME_CONSUMER_ID = 'shell.main';

const SHELL_THEME_VAR_KEYS = Object.freeze({
  shellBodyBg: '--shell-body-bg',
  shellSurface: '--shell-surface',
  shellBorder: '--shell-border',
  shellStatusBg: '--shell-status-bg',
  shellStatusBorder: '--shell-status-border',
  shellNavActiveBorder: '--shell-nav-active-border',
  shellNavActiveBg: '--shell-nav-active-bg',
  shellNavInactiveBorder: '--shell-nav-inactive-border',
  shellNavInactiveBg: '--shell-nav-inactive-bg',
  shellNavText: '--shell-nav-text',
  shellLogoutBorder: '--shell-logout-border',
  shellLogoutBg: '--shell-logout-bg',
  shellLogoutText: '--shell-logout-text',
  line: '--line',
  text: '--text',
  muted: '--muted'
});

function normalizeThemeModeId(themeId) {
  return isThemeModeId(themeId)
    ? String(themeId).trim().toLowerCase()
    : getDefaultThemeModeId();
}

function hasLiveRoot(rootEl) {
  return Boolean(rootEl && rootEl.style && typeof rootEl.style.setProperty === 'function');
}

function buildShellThemeTokens(tokens = {}) {
  const normalizedTokens = validateThemeTokens(tokens);
  return {
    shellBodyBg: normalizedTokens.bg,
    shellSurface: normalizedTokens.surface1,
    shellBorder: normalizedTokens.border,
    shellStatusBg: normalizedTokens.surface2,
    shellStatusBorder: normalizedTokens.border,
    shellNavActiveBorder: normalizedTokens.primary,
    shellNavActiveBg: normalizedTokens.accentSoft,
    shellNavInactiveBorder: normalizedTokens.border,
    shellNavInactiveBg: normalizedTokens.surface1,
    shellNavText: normalizedTokens.text,
    shellLogoutBorder: normalizedTokens.danger,
    shellLogoutBg: normalizedTokens.surface1,
    shellLogoutText: normalizedTokens.danger,
    line: normalizedTokens.border,
    text: normalizedTokens.text,
    muted: normalizedTokens.textSubtle
  };
}

export function mapTokensToShellVars(tokens = {}) {
  const shellTokens = buildShellThemeTokens(tokens);
  const mapped = {};

  Object.entries(SHELL_THEME_VAR_KEYS).forEach(([tokenKey, cssVarName]) => {
    mapped[cssVarName] = shellTokens[tokenKey];
  });

  return mapped;
}

export function clearShellThemePreview(rootEl) {
  if (!rootEl || typeof rootEl !== 'object') return;
  if (rootEl.dataset && typeof rootEl.dataset === 'object') {
    delete rootEl.dataset.kgShellThemePreview;
  }
  if (typeof rootEl.removeAttribute === 'function') {
    rootEl.removeAttribute('data-kg-shell-theme-preview');
  }
}

export function applyShellThemeMode(rootEl, themeId, options = {}) {
  if (!hasLiveRoot(rootEl)) {
    throw new Error('A live shell root element is required to apply a shell theme mode.');
  }

  const normalizedThemeId = normalizeThemeModeId(themeId);
  const themeDefinition = getThemeModeDefinition(normalizedThemeId);
  const shellVars = mapTokensToShellVars(themeDefinition.tokens);

  Object.entries(shellVars).forEach(([name, value]) => {
    rootEl.style.setProperty(name, value);
  });

  if (rootEl.dataset && typeof rootEl.dataset === 'object') {
    rootEl.dataset.kgShellThemeMode = normalizedThemeId;
    if (options.mode === 'preview') {
      rootEl.dataset.kgShellThemePreview = 'true';
    } else {
      delete rootEl.dataset.kgShellThemePreview;
    }
  }

  if (typeof rootEl.setAttribute === 'function') {
    rootEl.setAttribute('data-kg-shell-theme-mode', normalizedThemeId);
    if (options.mode === 'preview') {
      rootEl.setAttribute('data-kg-shell-theme-preview', 'true');
    } else {
      rootEl.removeAttribute?.('data-kg-shell-theme-preview');
    }
  }

  return {
    themeId: normalizedThemeId,
    vars: shellVars
  };
}

export function registerShellThemeConsumer(rootEl, options = {}) {
  if (!hasLiveRoot(rootEl)) {
    throw new Error('A live shell root element is required to register the shell theme consumer.');
  }

  let appliedThemeModeId = normalizeThemeModeId(options.appliedThemeModeId);
  let previewThemeModeId = null;
  const runtime = {
    rootElRef: rootEl
  };

  applyShellThemeMode(runtime.rootElRef, appliedThemeModeId, { mode: 'apply' });

  const consumerDefinition = {
    consumerId: SHELL_THEME_CONSUMER_ID,
    label: 'Shell',
    kind: 'shell',
    scopeGroup: 'platform',
    supportsThemeModes: true,
    priority: Number.isFinite(options.priority) ? Number(options.priority) : 90,
    globalThemeReady: true,
    rootElRef: runtime.rootElRef,
    applyThemeMode(nextThemeModeId, applyOptions = {}) {
      if (!hasLiveRoot(runtime.rootElRef)) {
        throw new Error('The shell theme consumer is missing its current root element.');
      }
      const normalizedThemeModeId = normalizeThemeModeId(nextThemeModeId);
      const mode = applyOptions && applyOptions.mode === 'preview' ? 'preview' : 'apply';
      applyShellThemeMode(runtime.rootElRef, normalizedThemeModeId, { mode });
      if (mode === 'preview') {
        previewThemeModeId = normalizedThemeModeId;
        return;
      }
      appliedThemeModeId = normalizedThemeModeId;
      previewThemeModeId = null;
      clearShellThemePreview(runtime.rootElRef);
    },
    clearThemePreview() {
      if (!hasLiveRoot(runtime.rootElRef) || previewThemeModeId == null) {
        return;
      }
      applyShellThemeMode(runtime.rootElRef, appliedThemeModeId, { mode: 'apply' });
      previewThemeModeId = null;
      clearShellThemePreview(runtime.rootElRef);
    }
  };

  registerThemeConsumer(consumerDefinition);

  return {
    consumerId: SHELL_THEME_CONSUMER_ID,
    updateRootElRef(nextRootEl) {
      if (!hasLiveRoot(nextRootEl)) {
        throw new Error('A live shell root element is required to refresh the shell theme consumer root.');
      }
      runtime.rootElRef = nextRootEl;
      registerThemeConsumer({
        ...consumerDefinition,
        rootElRef: runtime.rootElRef
      });
      applyShellThemeMode(runtime.rootElRef, appliedThemeModeId, { mode: 'apply' });
    },
    unregister() {
      unregisterThemeConsumer(SHELL_THEME_CONSUMER_ID);
    }
  };
}

export function unregisterShellThemeConsumer() {
  unregisterThemeConsumer(SHELL_THEME_CONSUMER_ID);
}

function assertShellThemeAdapterContract() {
  const mapped = mapTokensToShellVars({ bg: 42 });
  if (Object.keys(mapped).length !== Object.keys(SHELL_THEME_VAR_KEYS).length) {
    throw new Error('Shell theme variable mapping must stay complete.');
  }
  if (!Object.prototype.hasOwnProperty.call(mapped, '--shell-body-bg')) {
    throw new Error('Shell theme variable mapping must include --shell-body-bg.');
  }
  if (THEME_TOKEN_KEYS.length < 10) {
    throw new Error('Shell theme adapter expects the shared theme token schema.');
  }
}

assertShellThemeAdapterContract();
